/**
 * U-03: the one way an insurer's logo is drawn on a broker screen.
 *
 * `Insurer.logo` (a real `assets.MediaAsset` FK) and `effective_logo_url` have
 * existed for months and all 32 active insurers have a picture -- the broker
 * screens simply never rendered one. Four of them do now (Policies list, broker
 * Insurance Companies, a contact's policies, the insurer header), and the rule
 * they share lives here rather than being copied four times:
 *
 *  1. There is never an `<img>` with nothing behind it. No url, a url that 404s
 *     at runtime -- both collapse to the same icon fallback, so a torn-image
 *     glyph is unreachable. This mirrors `ContactAvatar`, for the same reason.
 *  2. Every picture carries an alt. It is "<name> logo" and not the bare name,
 *     because on every screen here the company's name is already visible text
 *     beside the mark; announcing the name twice is noise, and an empty alt
 *     would lose the fact that a logo is what is there.
 */

import { useState } from 'react';
import { BuildingLibraryIcon } from '@heroicons/react/24/outline';

import { cn } from '@/utils';

export type InsurerLogoSize = 'xs' | 'sm' | 'md' | 'lg';

/** Box, glyph and the intrinsic width/height that stop the row reflowing when
 *  the picture decodes. */
const SIZE_STYLES: Record<InsurerLogoSize, { box: string; glyph: string; px: number }> = {
  xs: { box: 'w-5 h-5 rounded', glyph: 'w-3 h-3', px: 20 },
  sm: { box: 'w-8 h-8 rounded-lg', glyph: 'w-4 h-4', px: 32 },
  md: { box: 'w-10 h-10 rounded-lg', glyph: 'w-5 h-5', px: 40 },
  lg: { box: 'w-16 h-16 rounded-lg', glyph: 'w-8 h-8', px: 64 },
};

interface InsurerLogoProps {
  /** `logo_display_url` / `insurer_logo_url` off the API row. '' or null simply
   *  means "this company has no picture", which is not an error. */
  src?: string | null;
  /** The company's name -- supplies the alt text and the hover title. */
  name?: string | null;
  /** U-05: the insurer's short blurb (`insurer_description`). When present it
   *  joins the hover title, so every screen that draws the mark can surface
   *  the blurb without a layout of its own; '' / absent adds nothing -- which
   *  is every live insurer today. Never read aloud: the alt stays the name. */
  description?: string | null;
  size?: InsurerLogoSize;
  className?: string;
}

export function InsurerLogo({ src, name, description, size = 'sm', className }: InsurerLogoProps) {
  // The src that failed, not a boolean: the moment a replacement logo arrives
  // the <img> re-arms itself with no reset effect.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const styles = SIZE_STYLES[size];
  const url = src || '';
  const showImage = Boolean(url) && failedSrc !== url;
  const label = `${name || 'Insurance company'} logo`;
  const blurb = (description || '').trim();
  const hoverTitle = blurb ? (name ? `${name} — ${blurb}` : blurb) : name || undefined;

  if (showImage) {
    return (
      <img
        src={url}
        alt={label}
        title={hoverTitle}
        width={styles.px}
        height={styles.px}
        loading="lazy"
        decoding="async"
        onError={() => setFailedSrc(url)}
        className={cn(
          'shrink-0 object-contain bg-gray-100 p-0.5 dark:bg-gray-700',
          styles.box,
          className
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        'shrink-0 inline-flex items-center justify-center bg-primary-100 dark:bg-primary-900/30',
        styles.box,
        className
      )}
      title={hoverTitle}
      // Decorative: the name is always rendered beside this, so the fallback
      // must not announce itself a second time.
      aria-hidden="true"
      data-testid="insurer-logo-fallback"
    >
      <BuildingLibraryIcon className={cn('text-primary-600 dark:text-primary-400', styles.glyph)} />
    </span>
  );
}
